import React from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  Select,
  Badge,
  Tooltip,
  Typography,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import type { PendingTransactionsSectionProps } from "./types";

export const PendingTransactionsSection: React.FC<PendingTransactionsSectionProps> = ({
  filteredTransactions,
  clients,
  custodians,
  clientFilter,
  setClientFilter,
  custodianFilter,
  setCustodianFilter,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  handleOpenSubmitBundle,
  deleteTransaction,
  getTypeLabel,
}) => {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={500}>
          Pending Transactions
        </Typography>
        <Badge badgeContent={filteredTransactions.length} color="primary">
          <Box />
        </Badge>
      </Box>

      {/* Filters Row */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Clients</MenuItem>
            {clients.map((c) => (
              <MenuItem key={c.client_id} value={c.client_id}>
                {c.client_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={custodianFilter}
            onChange={(e) => setCustodianFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Custodians</MenuItem>
            {custodians.map((c) => (
              <MenuItem key={c.custodian_id} value={c.custodian_id}>
                {c.custodian_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="trade">Trade</MenuItem>
            <MenuItem value="fx_spot">FX Spot</MenuItem>
            <MenuItem value="wire_transfer">Wire Transfer</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="validated">Validated</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>

      </Box>

      {/* Transactions Table */}
      {filteredTransactions.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">
            No transactions to display
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.map((txn) => (
                <TableRow key={txn.identifier}>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(txn.transaction_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{txn.client_name}</TableCell>
                  <TableCell>
                    {txn.transaction_type === "trade" && (
                      <>
                        {txn.side?.toUpperCase()} {txn.instrument_name || txn.instrument_isin}
                      </>
                    )}
                    {txn.transaction_type === "fx_spot" && (
                      <>
                        {txn.buy_currency}/{txn.sell_currency}
                      </>
                    )}
                    {txn.transaction_type === "wire_transfer" && (
                      <>
                        {txn.from_custodian_name || "External"} {" -> "}
                        {txn.to_custodian_name || "External"}
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: txn.currency,
                    }).format(txn.amount)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenSubmitBundle(txn)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          minWidth: 110,
                        }}
                      >
                        Submit Bundle
                      </Button>
                      <Tooltip title="Delete transaction">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteTransaction(txn.identifier)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};
